import html2canvas from 'html2canvas';

interface EnergyData {
  jahresPotenzial: number;
  einwohner: number;
  haushalte: number;
  energieQuellen: Array<{
    name: string;
    anzahl: number;
    kwh: number;
    potentialEuro: number;
  }>;
}

interface InfographicOptions {
  includeWappen?: boolean;
  includeLogo?: boolean;
  backgroundColor?: string;
  accentColor?: string;
  wappenUrl?: string;
}

export async function generateInfographicWithWappen(
  gemeindeId: string,
  gemeindeName: string,
  data: EnergyData,
  options: InfographicOptions = {}
): Promise<void> {
  const {
    includeWappen = true,
    includeLogo = true,
    backgroundColor = '#ffffff',
    accentColor = '#059669',
    wappenUrl
  } = options;

  // Use the provided wappenUrl
  const finalWappenUrl = includeWappen && wappenUrl ? wappenUrl : null;

  // Create a temporary container for the infographic
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.width = '800px';
  container.style.height = '1200px';
  container.style.backgroundColor = backgroundColor;
  container.style.fontFamily = 'Arial, sans-serif';
  
  // Create the infographic HTML
  container.innerHTML = `
    <div style="width: 100%; height: 100%; padding: 40px; box-sizing: border-box; background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);">
      <!-- Header with Wappen and Logo -->
      <div style="text-align: center; margin-bottom: 40px;">
        <div style="background: ${accentColor}; color: white; padding: 20px; border-radius: 15px; margin-bottom: 20px; position: relative;">
          ${finalWappenUrl ? `
            <div style="position: absolute; left: 20px; top: 50%; transform: translateY(-50%); background: white; padding: 8px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
              <img src="${finalWappenUrl}" alt="${gemeindeName} Wappen" style="width: 60px; height: 60px; object-fit: contain;">
            </div>
          ` : ''}
          ${includeLogo ? `
            <div style="position: absolute; right: 20px; top: 50%; transform: translateY(-50%);">
              <img src="/logo-white.svg" alt="Bürgerstrom Logo" style="width: 100px; height: auto;">
            </div>
          ` : ''}
          <h1 style="margin: 0; font-size: 28px; font-weight: bold;">ENERGIEGEMEINSCHAFT</h1>
          <h2 style="margin: 10px 0 0 0; font-size: 24px;">${gemeindeName}</h2>
        </div>
      </div>

      <!-- Main Potential -->
      <div style="background: white; border-radius: 20px; padding: 30px; text-align: center; margin-bottom: 30px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
        <div style="color: ${accentColor}; font-size: 18px; font-weight: bold; margin-bottom: 10px;">JÄHRLICHES POTENZIAL</div>
        <div style="color: ${accentColor}; font-size: 48px; font-weight: bold; margin-bottom: 15px;">€${data.jahresPotenzial.toLocaleString('de-DE')}</div>
        <div style="display: flex; justify-content: space-around; color: #6b7280; font-size: 14px;">
          <div>
            <div style="font-weight: bold; color: #1f2937;">€${Math.round(data.jahresPotenzial / data.einwohner)}</div>
            <div>pro Einwohner</div>
          </div>
          <div>
            <div style="font-weight: bold; color: #1f2937;">€${Math.round(data.jahresPotenzial / data.haushalte)}</div>
            <div>pro Haushalt</div>
          </div>
        </div>
      </div>

      <!-- Gemeinde Stats -->
      <div style="display: flex; gap: 20px; margin-bottom: 30px;">
        <div style="flex: 1; background: white; border-radius: 15px; padding: 20px; text-align: center; box-shadow: 0 5px 15px rgba(0,0,0,0.08);">
          <div style="color: #3b82f6; font-size: 32px; font-weight: bold;">${data.einwohner.toLocaleString('de-DE')}</div>
          <div style="color: #6b7280; font-size: 14px;">Einwohner</div>
        </div>
        <div style="flex: 1; background: white; border-radius: 15px; padding: 20px; text-align: center; box-shadow: 0 5px 15px rgba(0,0,0,0.08);">
          <div style="color: #8b5cf6; font-size: 32px; font-weight: bold;">${data.haushalte.toLocaleString('de-DE')}</div>
          <div style="color: #6b7280; font-size: 14px;">Haushalte</div>
        </div>
      </div>

      <!-- Energy Sources -->
      <div style="background: white; border-radius: 20px; padding: 25px; margin-bottom: 30px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
        <h3 style="margin: 0 0 20px 0; color: #1f2937; font-size: 18px; text-align: center;">ENERGIEQUELLEN</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
          ${data.energieQuellen.map(source => `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px; background: #f9fafb; border-radius: 10px;">
              <div>
                <div style="font-weight: bold; color: #1f2937; font-size: 14px;">${source.name}</div>
                <div style="color: #6b7280; font-size: 12px;">${source.anzahl} Anlagen</div>
              </div>
              <div style="text-align: right;">
                <div style="font-weight: bold; color: ${accentColor}; font-size: 14px;">€${source.potentialEuro.toLocaleString('de-DE')}</div>
                <div style="color: #6b7280; font-size: 11px;">${Math.round(source.kwh).toLocaleString('de-DE')} kWh</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Benefits -->
      <div style="background: linear-gradient(135deg, ${accentColor}, ${adjustColor(accentColor, -20)}); color: white; border-radius: 20px; padding: 25px; margin-bottom: 20px;">
        <h3 style="margin: 0 0 15px 0; font-size: 18px; text-align: center;">VORTEILE FÜR IHRE BÜRGER</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; text-align: center;">
          <div>
            <div style="font-size: 24px; font-weight: bold;">€147</div>
            <div style="font-size: 12px; opacity: 0.9;">Durchschnittliche Ersparnis/Jahr</div>
          </div>
          <div>
            <div style="font-size: 24px; font-weight: bold;">€384</div>
            <div style="font-size: 12px; opacity: 0.9;">Mit eigener PV-Anlage/Jahr</div>
          </div>
          <div>
            <div style="font-size: 24px; font-weight: bold;">€1.250</div>
            <div style="font-size: 12px; opacity: 0.9;">Gewerbebetrieb/Jahr</div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div style="text-align: center; color: #6b7280; font-size: 12px;">
        <div style="margin-bottom: 5px;">Erstellt mit Bürgerstrom Dashboard</div>
        <div>www.buergerstrom.at | ${new Date().toLocaleDateString('de-DE')}</div>
      </div>
    </div>
  `;
  
  // Add to DOM temporarily
  document.body.appendChild(container);
  
  try {
    // Generate canvas from HTML
    const canvas = await html2canvas(container, {
      width: 800,
      height: 1200,
      scale: 2, // Higher resolution
      backgroundColor: backgroundColor,
      useCORS: true,
      logging: false
    });
    
    // Convert to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `infografik-${gemeindeName.toLowerCase().replace(/\s/g, '-')}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }, 'image/png', 1.0);
    
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
}

// Helper function to adjust color brightness
function adjustColor(color: string, amount: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount));
  const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}